export const userPermissionCheck = (
  loginAccount: Record<string, any>,
  fcName: string,
  isMute: boolean = false
): boolean => {
  const warn = (...args: unknown[]) => {
    if (!isMute) {
      console.warn(...args);
    }
  };

  if (loginAccount?.account_id === -1) {
    return true;
  }
  if (!loginAccount?.user_auth_passed) {
    warn('permission check user_auth_passed failed', loginAccount);
    return false;
  }
  const portal = getPortal(loginAccount, fcName);
  if (!portal) {
    warn('permission check portal not found', loginAccount, fcName);
    return false;
  }
  const access_type = getPortalAccessType(portal);

  if (
    !portal?.is_allow_access ||
    !access_type ||
    ['Denied', 'Undefined'].includes(access_type)
  ) {
    warn(
      'permission check portal failed: portal.is_allow_access:',
      portal?.is_allow_access,
      '; user_role:',
      portal?.user_role,
      '; user_permission:',
      portal?.user_permission,
      '; portal.permission_profile:',
      JSON.stringify(portal?.permission_profile)
    );
    return false;
  }

  return true;
};

const getPortal = (
  account: Record<string, any>,
  fcName: string
): Record<string, any> | undefined =>
  account?.portals?.find(
    (obj: Record<string, any>) => obj?.app_name === fcName
  );

// patch of 0172 for portal.permission_profile is null
const getPortalAccessType = (portal?: Record<string, any>): string =>
  portal?.user_role ||
  portal?.user_permission ||
  portal?.permission_profile?.access_type ||
  '';

export const IsFullAccess = (
  account: Record<string, any>,
  fcName: string
): boolean => {
  const access_type = getPortalAccessType(getPortal(account, fcName));
  return access_type.toLowerCase().includes('admin');
};

// FC test external IDP account user_id could be 0
const isDefined = (value: unknown) =>
  value !== null && value !== undefined && value !== '';

export const isSameAccount = (
  account?: Record<string, any>,
  permission?: Record<string, any>
) => {
  if (!account || !permission) {
    return false;
  }

  if (account.account_id !== permission.account_id) {
    return false;
  }

  if (isDefined(account.user_id) && account.user_id !== permission.user_id) {
    return false;
  }

  if (
    isDefined(account.account_node_id) &&
    isDefined(permission.account_node_id) &&
    account.account_node_id !== permission.account_node_id
  ) {
    return false;
  }

  return true;
};

export const getRowFullAccess = (
  rowData: Record<string, any>,
  accountWithPermission: Record<string, any>,
  fcName?: string
): boolean =>
  IsFullAccess(
    rowData?.portals?.length || !isSameAccount(rowData, accountWithPermission)
      ? rowData
      : accountWithPermission,
    fcName || ''
  );

// `fc/user_org_nodes` returns the same account once per granting identity (same
// account_id / account_node_id, another `access_from` user, account and role).
// A lookup normalizes every row, keeps those of the requested account granted by
// the requested identity, then picks the closest one.

const toStr = (value: unknown): string => (isDefined(value) ? `${value}` : '');

// 0 is not a sub account identity (FC external IDP accounts use it)
const toSubUserId = (value: unknown): string => {
  const userId = toStr(value);
  return Number(userId) > 0 ? userId : '';
};

// A row / OU tree node / redirect object as strings, '' meaning "not known".
interface AccountIdentity {
  accountId: string;
  accountNodeId: string;
  userId: string;
  subUserId: string;
  grantingUserId: string;
  grantingAccountId: string;
  orgId: string;
}

const readIdentity = (obj?: Record<string, any>): AccountIdentity => ({
  accountId: toStr(obj?.account_id),
  accountNodeId: toStr(obj?.account_node_id ?? obj?.node_id),
  userId: toStr(obj?.user_id),
  subUserId: toSubUserId(obj?.user_id),
  grantingUserId: toStr(obj?.access_from_user_id ?? obj?.access_from?.user_id),
  grantingAccountId: toStr(
    obj?.access_from_account_id ?? obj?.access_from?.account_id
  ),
  orgId: toStr(obj?.org_id)
});

// Only a value known on both sides is compared.
const isSameValue = (a: string, b: string): boolean => !!a && !!b && a === b;
const isDifferentValue = (a: string, b: string): boolean =>
  !!a && !!b && a !== b;

const hasGrantingIdentity = (identity: AccountIdentity): boolean =>
  !!identity.grantingUserId || !!identity.grantingAccountId;

// 1. scope: same account, granted by the same identity (or by none on both sides)
const isInAccountScope = (
  row: AccountIdentity,
  identity: AccountIdentity
): boolean =>
  isSameValue(row.accountId, identity.accountId) &&
  hasGrantingIdentity(row) === hasGrantingIdentity(identity) &&
  !isDifferentValue(row.grantingUserId, identity.grantingUserId) &&
  !isDifferentValue(row.grantingAccountId, identity.grantingAccountId);

// 2. pick: the identity fields, most significant first
const isSameAccountNodeId = (row: AccountIdentity, identity: AccountIdentity) =>
  isSameValue(row.accountNodeId, identity.accountNodeId);

const isSameSubUser = (row: AccountIdentity, identity: AccountIdentity) =>
  isSameValue(row.subUserId, identity.subUserId);

const isSameGrantingIdentity = (
  row: AccountIdentity,
  identity: AccountIdentity
) =>
  isSameValue(row.grantingUserId, identity.grantingUserId) ||
  isSameValue(row.grantingAccountId, identity.grantingAccountId);

const isSameOrg = (row: AccountIdentity, identity: AccountIdentity) =>
  isSameValue(row.orgId, identity.orgId);

const IDENTITY_FIELDS = [
  isSameAccountNodeId,
  isSameSubUser,
  isSameGrantingIdentity,
  isSameOrg
];

// The first field that tells two rows apart decides, ties keep the first one.
const isCloserMatch = (
  row: AccountIdentity,
  current: AccountIdentity,
  identity: AccountIdentity
): boolean => {
  for (const matches of IDENTITY_FIELDS) {
    const isMatch = matches(row, identity);
    if (isMatch !== matches(current, identity)) {
      return isMatch;
    }
  }
  return false;
};

// The identity `requireIdentity` asks for: the account node id, or `user_id`
// known on both sides (0 / -1 included, master and FC side rows have no node id).
const isSameUserId = (row: AccountIdentity, identity: AccountIdentity) =>
  isSameValue(row.userId, identity.userId);

const hasAccountIdentity = (
  row: AccountIdentity,
  identity: AccountIdentity
): boolean => isSameAccountNodeId(row, identity) || isSameUserId(row, identity);

// Depth first in document order, keeping the in-scope items only.
const collectInAccountScope = <T>(
  items: T[],
  rowOf: (item: T) => AccountIdentity,
  getChildren: ((item: T) => T[] | undefined) | undefined,
  identity: AccountIdentity
): T[] => {
  const inScope: T[] = [];
  const stack: T[] = [...items].reverse();
  while (stack.length) {
    const item = stack.pop() as T;
    if (isInAccountScope(rowOf(item), identity)) {
      inScope.push(item);
    }
    const children = getChildren?.(item);
    if (children?.length) {
      for (let i = children.length - 1; i >= 0; i--) {
        stack.push(children[i]);
      }
    }
  }
  return inScope;
};

interface IAccountMatchOptions<T> {
  // an item is either an account row or a wrapper around one (an OU tree node)
  getRow?: (item: T) => Record<string, any> | undefined;
  getChildren?: (item: T) => T[] | undefined;
  // refuse a match that only has the account id in common
  requireIdentity?: boolean;
}

export const findAccountMatch = <T>(
  items: T[] | undefined,
  identity?: Record<string, any>,
  options?: IAccountMatchOptions<T>
): T | undefined => {
  if (!items?.length || !identity) {
    return undefined;
  }

  const { getChildren, getRow, requireIdentity } = options || {};
  const reference = readIdentity(identity);
  const rowOf = (item: T) =>
    readIdentity(getRow ? getRow(item) : (item as Record<string, any>));

  // 1. scope
  const inScope = collectInAccountScope(items, rowOf, getChildren, reference);
  if (!inScope.length) {
    return undefined;
  }

  // 2. closest of them
  const best = inScope.reduce((closest, item) =>
    isCloserMatch(rowOf(item), rowOf(closest), reference) ? item : closest
  );

  // 3. account_id only is not an identity when the caller asks for one
  if (requireIdentity && !hasAccountIdentity(rowOf(best), reference)) {
    return undefined;
  }

  return best;
};
